import {
  useState, useRef, useEffect, useCallback, useMemo,
} from "react";
import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { supabase } from "@/lib/supabase";
import { isPartenaireVideo } from "@shared/partenaireVideos";
import { PartenaireVideoPlayer } from "@/components/partenaire-video-player";
import { uploadPartenaireVideo, confirmPartenaireVideo, type VideoUpload } from "@/lib/partenaire-video-upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  Folder, FolderOpen, FileText, File, Film, Image as ImageIcon, FileType,
  Plus, Pencil, Trash2, Download, Link2, Eye, Upload, Loader2,
  Megaphone, ToggleLeft, Grid3X3, List, Search, Home, ChevronRight,
  TreePine, X, GripVertical, MoveRight, CheckSquare, AlertCircle,
  Package, Tag, ArrowUp, ArrowDown, Gift,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────────────
type Categorie = {
  id: string; nom: string; parentId: string | null;
  ordre: number; icone?: string; createdAt: string;
  subfolders?: number; subdocs?: number;
};
type Document = {
  id: string; titre: string; description?: string; categorieId?: string;
  fichierUrl?: string; typeFichier?: string; visibilite: string;
  revendeursIds?: string[]; notifier: boolean; actif: boolean;
  ordre: number; tailleFichier?: number; createdAt: string; nbLectures: number;
  storageBucket?: string; mimeType?: string;
};
type Actualite = {
  id: string; titre: string; accroche?: string; contenu?: string;
  imageUrl?: string; documentId?: string; type: string;
  prixPromo?: string; prixNormal?: string; produitId?: string;
  visibilite: string; dateDebut?: string; dateFin?: string;
  actif: boolean; createdAt: string; nbLectures: number;
  isBundle?: boolean; documentPdfUrl?: string;
};
type ProductItem = {
  id: string; nomFr: string; sku: string; imageUrl?: string | null;
  prixHt?: string | null; type: string;
};
type LocalBundleItem = {
  rowId?: string;
  productId: string; nomFr: string; sku: string;
  imageUrl: string | null; quantite: number;
  prixUnitaire: string; offert: boolean;
};
type Reseller = { id: string; nom: string; typeContrat?: string };
type Lecture = { revendeurId: string; nom: string; luAt: string };
interface UploadEntry { name: string; progress: number; done: boolean; error?: string; }
type ContextItem = { id: string; type: "folder" | "document"; nom: string; data: Categorie | Document };
type SelectionKey = string; // "folder:id" | "document:id"
type BreadcrumbItem = { id: string | null; nom: string };

// ── Helpers ────────────────────────────────────────────────────────────────────
function invalidateDocumentCaches(qc: QueryClient) {
  // Les clés historiques sont tantôt segmentées, tantôt un chemin complet.
  qc.invalidateQueries({ predicate: query => String(query.queryKey[0]).startsWith("/api/partenaires") });
  qc.invalidateQueries({ queryKey: ["/api/portail/ressources"] });
}
function formatBytes(b?: number) {
  if (!b) return "";
  if (b < 1024) return `${b} o`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / 1048576).toFixed(1)} Mo`;
}
function formatDate(d?: string) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR");
}
function detectType(mime: string, ext: string) {
  if (mime.includes("pdf") || ext === "pdf") return "pdf";
  if (mime.includes("word") || ["doc", "docx"].includes(ext)) return "word";
  if (mime.includes("video") || ["mp4", "mov", "avi", "webm"].includes(ext)) return "video";
  if (mime.includes("image") || ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return "image";
  return "autre";
}
function buildBreadcrumb(cats: Categorie[], folderId: string | null): BreadcrumbItem[] {
  const root: BreadcrumbItem = { id: null, nom: "Espace partenaires" };
  if (!folderId) return [root];
  const path: BreadcrumbItem[] = [];
  let cur = cats.find(c => c.id === folderId);
  while (cur) {
    path.unshift({ id: cur.id, nom: cur.nom });
    cur = cur.parentId ? cats.find(c => c.id === cur!.parentId) : undefined;
  }
  return [root, ...path];
}
function flattenCategories(cats: Categorie[], parentId: string | null = null, depth = 0): Array<Categorie & { depth: number }> {
  return cats.filter(c => c.parentId === parentId).sort((a, b) => a.ordre - b.ordre)
    .flatMap(c => [{ ...c, depth }, ...flattenCategories(cats, c.id, depth + 1)]);
}

// ── File type icon + color ─────────────────────────────────────────────────────
function TypeIcon({ type, size = 40 }: { type?: string; size?: number }) {
  const cls = `shrink-0` as const;
  const s = { width: size, height: size } as const;
  if (type === "pdf") return <FileText style={s} className={`${cls} text-red-500`} />;
  if (type === "word") return <File style={s} className={`${cls} text-blue-500`} />;
  if (type === "video") return <Film style={s} className={`${cls} text-violet-500`} />;
  if (type === "image") return <ImageIcon style={s} className={`${cls} text-green-500`} />;
  return <FileType style={s} className={`${cls} text-muted-foreground`} />;
}

function VisiBadge({ v }: { v: string }) {
  const map: Record<string, string> = {
    tous: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300",
    revendeur_premium: "bg-amber-100 text-amber-600",
    sous_distributeur: "bg-sky-100 text-sky-600",
    individuel: "bg-purple-100 text-purple-600",
  };
  const labels: Record<string, string> = { tous: "Tous", revendeur_premium: "Premium", sous_distributeur: "Sous-distrib.", individuel: "Spécifiques" };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${map[v] || map.tous}`}>{labels[v] || v}</span>;
}

// ── Upload with XHR progress ───────────────────────────────────────────────────
async function uploadWithProgress(file: File, onProgress: (p: number) => void): Promise<{ path: string; size: number; mimetype: string; receipt?: string }> {
  if (isPartenaireVideo(file.name, file.type)) return uploadPartenaireVideo(file, onProgress);
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const fd = new FormData(); fd.append("file", file);
    xhr.upload.addEventListener("progress", e => { if (e.lengthComputable) onProgress(Math.round(e.loaded / e.total * 100)); });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
      else { try { reject(new Error(JSON.parse(xhr.responseText)?.message || `Erreur ${xhr.status}`)); } catch { reject(new Error(`Erreur ${xhr.status}`)); } }
    });
    xhr.addEventListener("error", () => reject(new Error("Erreur réseau")));
    xhr.open("POST", "/api/partenaires/documents/upload");
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.send(fd);
  });
}

// ── Context Menu ───────────────────────────────────────────────────────────────
function ContextMenu({ x, y, item, onClose, onDownload, onRename, onMove, onVisibility, onDelete, onPreview }: {
  x: number; y: number; item: ContextItem; onClose: () => void;
  onDownload?: () => void; onRename: () => void; onMove: () => void;
  onVisibility?: () => void; onDelete: () => void; onPreview?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);
  // Adjust position to stay in viewport
  const style: React.CSSProperties = { position: "fixed", zIndex: 9999, top: Math.min(y, window.innerHeight - 260), left: Math.min(x, window.innerWidth - 200) };
  const items = [
    item.type === "document" && onPreview && { label: "Aperçu", icon: <Eye className="w-3.5 h-3.5" />, action: onPreview },
    item.type === "document" && onDownload && { label: "Télécharger", icon: <Download className="w-3.5 h-3.5" />, action: onDownload },
    { label: "Renommer", icon: <Pencil className="w-3.5 h-3.5" />, action: onRename },
    { label: "Déplacer vers…", icon: <MoveRight className="w-3.5 h-3.5" />, action: onMove },
    item.type === "document" && onVisibility && { label: "Modifier la visibilité", icon: <CheckSquare className="w-3.5 h-3.5" />, action: onVisibility },
    { label: "Supprimer", icon: <Trash2 className="w-3.5 h-3.5" />, action: onDelete, danger: true },
  ].filter(Boolean) as { label: string; icon: JSX.Element; action: () => void; danger?: boolean }[];

  return (
    <div ref={ref} style={style} className="bg-card border rounded-lg shadow-xl py-1 min-w-[180px] text-sm" data-testid="context-menu">
      <div className="px-3 py-1.5 text-xs text-muted-foreground font-medium border-b mb-1 truncate">{item.nom}</div>
      {items.map(it => (
        <button key={it.label} onClick={() => { it.action(); onClose(); }}
          className={`flex items-center gap-2.5 w-full px-3 py-1.5 hover:bg-muted transition-colors ${it.danger ? "text-destructive" : ""}`}>
          {it.icon}{it.label}
        </button>
      ))}
    </div>
  );
}

// ── Tree Drawer ────────────────────────────────────────────────────────────────
function TreeDrawer({ open, onClose, categories, currentFolderId, onNavigate }: {
  open: boolean; onClose: () => void;
  categories: Categorie[]; currentFolderId: string | null;
  onNavigate: (id: string | null) => void;
}) {
  function TreeNode({ cat, depth }: { cat: Categorie; depth: number }) {
    const children = categories.filter(c => c.parentId === cat.id).sort((a, b) => a.ordre - b.ordre);
    const [open2, setOpen2] = useState(depth < 2);
    const isActive = cat.id === currentFolderId;
    return (
      <div>
        <div onClick={() => { setOpen2(o => !o); onNavigate(cat.id); }}
          style={{ paddingLeft: `${8 + depth * 14}px` }}
          className={`flex items-center gap-1.5 py-1 pr-2 rounded cursor-pointer text-sm select-none ${isActive ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted/50"}`}>
          {children.length > 0 ? (open2 ? <ChevronRight className="w-3 h-3 rotate-90 transition-transform" /> : <ChevronRight className="w-3 h-3 transition-transform" />) : <span className="w-3" />}
          <Folder className={`w-3.5 h-3.5 shrink-0 ${isActive ? "text-primary" : "text-amber-500"}`} />
          <span className="truncate">{cat.nom}</span>
        </div>
        {open2 && children.map(c => <TreeNode key={c.id} cat={c} depth={depth + 1} />)}
      </div>
    );
  }
  const roots = categories.filter(c => !c.parentId).sort((a, b) => a.ordre - b.ordre);
  return (
    <>
      {open && <div className="fixed inset-0 z-30" onClick={onClose} />}
      <div className={`fixed inset-y-0 left-0 z-40 w-72 bg-card border-r shadow-xl transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between p-3 border-b">
          <span className="font-semibold text-sm">Arborescence</span>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="overflow-y-auto h-[calc(100vh-56px)] p-2">
          <div onClick={() => onNavigate(null)}
            className={`flex items-center gap-1.5 py-1 px-2 rounded cursor-pointer text-sm select-none mb-1 ${!currentFolderId ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted/50"}`}>
            <Home className="w-3.5 h-3.5 shrink-0" /><span>Espace partenaires</span>
          </div>
          {roots.map(c => <TreeNode key={c.id} cat={c} depth={0} />)}
        </div>
      </div>
    </>
  );
}

// ── Upload Queue Panel ─────────────────────────────────────────────────────────
function UploadQueue({ uploads, onClose }: { uploads: UploadEntry[]; onClose: () => void }) {
  const allDone = uploads.every(u => u.done);
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-card border rounded-xl shadow-2xl overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
        <span className="text-sm font-medium">
          {allDone ? "Upload terminé" : `Envoi en cours… (${uploads.filter(u => u.done).length}/${uploads.length})`}
        </span>
        {allDone && <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClose}><X className="w-3.5 h-3.5" /></Button>}
      </div>
      <div className="max-h-52 overflow-y-auto p-2 space-y-2">
        {uploads.map((u, i) => (
          <div key={i} className="text-xs">
            <div className="flex justify-between mb-0.5 gap-2">
              <span className="truncate text-muted-foreground">{u.name}</span>
              {u.error ? <span className="text-destructive shrink-0">Erreur</span>
                : u.done ? <span className="text-green-600 shrink-0">✓</span>
                : <span className="text-muted-foreground shrink-0">{u.progress}%</span>}
            </div>
            <div className="h-1 rounded-full bg-muted overflow-hidden">
              <div className={`h-full rounded-full transition-all ${u.error ? "bg-destructive" : u.done ? "bg-green-500" : "bg-primary"}`}
                style={{ width: `${u.progress}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Move Dialog ────────────────────────────────────────────────────────────────
function MoveDialog({ open, onClose, items, categories, onMoved }: {
  open: boolean; onClose: () => void;
  items: { id: string; type: "folder" | "document"; nom: string }[];
  categories: Categorie[]; onMoved: () => void;
}) {
  const [destId, setDestId] = useState("root");
  const { toast } = useToast();
  const qc = useQueryClient();
  const flatCats = flattenCategories(categories);

  const move = async () => {
    const docIds = items.filter(i => i.type === "document").map(i => i.id);
    const catIds = items.filter(i => i.type === "folder").map(i => i.id);
    const target = destId === "root" ? null : destId;
    if (docIds.length > 0) {
      await authFetch("/api/partenaires/documents/batch-move", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: docIds, type: "document", target_category_id: target }),
      });
    }
    if (catIds.length > 0) {
      await authFetch("/api/partenaires/documents/batch-move", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: catIds, type: "category", target_category_id: target }),
      });
    }
    qc.invalidateQueries({ queryKey: ["/api/partenaires"] });
    toast({ title: `${items.length} élément(s) déplacé(s)` });
    onMoved(); onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Déplacer vers…</DialogTitle></DialogHeader>
        <div className="py-2 space-y-3">
          <p className="text-sm text-muted-foreground">{items.length} élément(s) sélectionné(s)</p>
          <Select value={destId} onValueChange={setDestId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="root">Espace partenaires (racine)</SelectItem>
              {flatCats.filter(c => !items.some(it => it.id === c.id)).map(c => (
                <SelectItem key={c.id} value={c.id}>
                  {"\u00a0".repeat(c.depth * 3)}{c.depth > 0 ? "└ " : ""}{c.nom}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={move}>Déplacer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Visibility Dialog ──────────────────────────────────────────────────────────
function VisibilityDialog({ open, onClose, docIds, resellers, onDone }: {
  open: boolean; onClose: () => void; docIds: string[];
  resellers: Reseller[]; onDone: () => void;
}) {
  const [visibilite, setVisibilite] = useState("tous");
  const [revendeursIds, setRevendeursIds] = useState<string[]>([]);
  const { toast } = useToast();
  const qc = useQueryClient();

  const save = async () => {
    await authFetch("/api/partenaires/documents/batch-visibility", {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: docIds, visibilite, revendeurs_ids: revendeursIds }),
    });
    qc.invalidateQueries({ queryKey: ["/api/partenaires"] });
    toast({ title: "Visibilité mise à jour" });
    onDone(); onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Modifier la visibilité</DialogTitle></DialogHeader>
        <div className="py-2 space-y-3">
          <p className="text-sm text-muted-foreground">{docIds.length} document(s) sélectionné(s)</p>
          <Select value={visibilite} onValueChange={setVisibilite}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tous">Tous les revendeurs</SelectItem>
              <SelectItem value="revendeur_premium">Premium uniquement</SelectItem>
              <SelectItem value="sous_distributeur">Sous-distributeurs</SelectItem>
              <SelectItem value="individuel">Revendeurs spécifiques</SelectItem>
            </SelectContent>
          </Select>
          {visibilite === "individuel" && (
            <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-1.5">
              {resellers.map(r => (
                <label key={r.id} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={revendeursIds.includes(r.id)}
                    onChange={e => setRevendeursIds(p => e.target.checked ? [...p, r.id] : p.filter(x => x !== r.id))} />
                  {r.nom}
                </label>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Document Edit Modal ────────────────────────────────────────────────────────
function DocumentModal({ open, onClose, editing, categories, resellers, currentFolderId, onSaved }: {
  open: boolean; onClose: () => void; editing: Document | null;
  categories: Categorie[]; resellers: Reseller[]; currentFolderId: string | null; onSaved: () => void;
}) {
  const [titre, setTitre] = useState(editing?.titre || "");
  const [description, setDescription] = useState(editing?.description || "");
  const [categorieId, setCategorieId] = useState(editing?.categorieId || currentFolderId || "");
  const [visibilite, setVisibilite] = useState(editing?.visibilite || "tous");
  const [revendeursIds, setRevendeursIds] = useState<string[]>(editing?.revendeursIds || []);
  const [notifier, setNotifier] = useState(editing?.notifier !== false);
  const [actif, setActif] = useState(editing?.actif !== false);
  const [fichierUrl, setFichierUrl] = useState(editing?.fichierUrl || "");
  const [typeFichier, setTypeFichier] = useState(editing?.typeFichier || "");
  const [tailleFichier, setTailleFichier] = useState(editing?.tailleFichier);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoUpload, setVideoUpload] = useState<VideoUpload | null>(null);
  const [documentUpload, setDocumentUpload] = useState<{ mimeType: string; nomOrigine: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const qc = useQueryClient();
  const flatCats = flattenCategories(categories);

  const handleFile = async (f: File) => {
    setUploading(true);
    setUploadProgress(0);
    try {
      const ext = f.name.split(".").pop()?.toLowerCase() || "";
      const data = await uploadWithProgress(f, setUploadProgress);
      if (videoUpload) console.warn("[partenaire-videos] upload remplacé avant confirmation", videoUpload.path);
      setVideoUpload(data.receipt ? data as VideoUpload : null);
      setDocumentUpload(data.receipt ? null : { mimeType: data.mimetype, nomOrigine: f.name });
      setFichierUrl(data.path); setTypeFichier(detectType(f.type, ext)); setTailleFichier(data.size);
    } catch (error: any) {
      console.error("[partenaires] upload", error);
      toast({ title: error.message || "Erreur d'upload", variant: "destructive" });
    }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!titre.trim() || uploading || saving) return;
    setSaving(true);
    try {
    const metadata = { titre, description, categorieId: categorieId || null, fichierUrl, typeFichier, visibilite, revendeursIds: visibilite === "individuel" ? revendeursIds : [], notifier, actif, tailleFichier, ...documentUpload };
    if (videoUpload) {
      await confirmPartenaireVideo(videoUpload, { ...metadata, documentId: editing?.id });
    } else {
    const url = editing ? `/api/partenaires/documents/${editing.id}` : "/api/partenaires/documents";
    const res = await authFetch(url, {
      method: editing ? "PUT" : "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(metadata),
    });
    if (!res.ok) { const error = await res.json(); throw new Error(error.message || "Enregistrement impossible"); }
    }
    invalidateDocumentCaches(qc);
    onSaved(); onClose();
    } catch (error: any) {
      toast({ title: error.message || "Erreur", variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => {
      if (uploading || saving) return;
      if (videoUpload) console.warn("[partenaire-videos] modale fermée sans confirmation", videoUpload.path);
      onClose();
    }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Modifier le document" : "Nouveau document"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <div><Label>Titre *</Label><Input value={titre} onChange={e => setTitre(e.target.value)} /></div>
          <div><Label>Description</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} /></div>
          <div>
            <Label>Catégorie</Label>
            <Select value={categorieId || "none"} onValueChange={v => setCategorieId(v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Sans catégorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sans catégorie</SelectItem>
                {flatCats.map(c => <SelectItem key={c.id} value={c.id}>{"\u00a0".repeat(c.depth * 3)}{c.depth > 0 ? "└ " : ""}{c.nom}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Visibilité</Label>
            <Select value={visibilite} onValueChange={setVisibilite}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les revendeurs</SelectItem>
                <SelectItem value="revendeur_premium">Premium uniquement</SelectItem>
                <SelectItem value="sous_distributeur">Sous-distributeurs</SelectItem>
                <SelectItem value="individuel">Revendeurs spécifiques</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {visibilite === "individuel" && (
            <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-1.5">
              {resellers.map(r => (
                <label key={r.id} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={revendeursIds.includes(r.id)}
                    onChange={e => setRevendeursIds(p => e.target.checked ? [...p, r.id] : p.filter(x => x !== r.id))} />
                  {r.nom}
                </label>
              ))}
            </div>
          )}
          <div>
            <Label>Fichier</Label>
            <div className="flex gap-2 mt-1">
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading || saving} className="gap-2">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? `Envoi… ${uploadProgress} %` : "Choisir"}
              </Button>
              {fichierUrl && <span className="text-xs text-muted-foreground self-center truncate">{fichierUrl}</span>}
            </div>
            <input ref={fileRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            {uploading && <progress className="mt-2 w-full" max={100} value={uploadProgress} aria-label="Progression de l'envoi" />}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2"><Switch id="notif" checked={notifier} onCheckedChange={setNotifier} /><Label htmlFor="notif">Notifier</Label></div>
            <div className="flex items-center gap-2"><Switch id="actif2" checked={actif} onCheckedChange={setActif} /><Label htmlFor="actif2">Actif</Label></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" disabled={uploading || saving} onClick={() => {
            if (videoUpload) console.warn("[partenaire-videos] annulation avant confirmation", videoUpload.path);
            onClose();
          }}>Annuler</Button>
          <Button onClick={save} disabled={uploading || saving}>{saving ? "Enregistrement…" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Lectures Panel ─────────────────────────────────────────────────────────────
function LecturesPanel({ open, onClose, docId, actId }: { open: boolean; onClose: () => void; docId?: string; actId?: string }) {
  const url = docId ? `/api/partenaires/documents/${docId}/lectures` : `/api/partenaires/actualites/${actId}/lectures`;
  const { data: lectures = [], isLoading } = useQuery<Lecture[]>({
    queryKey: [url], queryFn: async () => { const r = await authFetch(url); return r.json(); }, enabled: open,
  });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Lectures ({lectures.length})</DialogTitle></DialogHeader>
        <div className="max-h-80 overflow-y-auto">
          {isLoading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          {!isLoading && lectures.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Aucune lecture.</p>}
          {lectures.map((l, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
              <span className="font-medium">{l.nom}</span>
              <span className="text-xs text-muted-foreground">{formatDate(l.luAt)}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Actualité Modal ────────────────────────────────────────────────────────────
function ActualiteModal({ open, onClose, editing, documents, onSaved }: {
  open: boolean; onClose: () => void; editing: Actualite | null; documents: Document[]; onSaved: () => void;
}) {
  const [titre, setTitre] = useState(editing?.titre || "");
  const [accroche, setAccroche] = useState(editing?.accroche || "");
  const [contenu, setContenu] = useState(editing?.contenu || "");
  const [type, setType] = useState(editing?.type || "information");
  const [visibilite, setVisibilite] = useState(editing?.visibilite || "tous");
  const [actif, setActif] = useState(editing?.actif !== false);
  const [prixPromo, setPrixPromo] = useState(editing?.prixPromo || "");
  const [prixNormal, setPrixNormal] = useState(editing?.prixNormal || "");
  const [dateDebut, setDateDebut] = useState(editing?.dateDebut?.substring(0, 10) || "");
  const [dateFin, setDateFin] = useState(editing?.dateFin?.substring(0, 10) || "");
  const [documentId, setDocumentId] = useState(editing?.documentId || "");
  const [imageUrl, setImageUrl] = useState(editing?.imageUrl || "");
  const [uploading, setUploading] = useState(false);
  // Bundle
  const [isBundle, setIsBundle] = useState(editing?.isBundle || false);
  const [documentPdfUrl, setDocumentPdfUrl] = useState(editing?.documentPdfUrl || "");
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [localBundle, setLocalBundle] = useState<LocalBundleItem[]>([]);
  const [productSearch, setProductSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const imgRef = useRef<HTMLInputElement>(null);
  const pdfRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const qc = useQueryClient();

  // Fetch produits disponibles (uniquement quand type=promotion)
  const { data: allProducts = [] } = useQuery<ProductItem[]>({
    queryKey: ["/api/products"],
    enabled: type === "promotion",
  });

  // Fetch bundle items existants (uniquement en édition)
  const { data: serverBundleItems = [] } = useQuery<(LocalBundleItem & { rowId: string; prixHt?: string })[]>({
    queryKey: ["/api/partenaires/actualites", editing?.id, "bundle"],
    queryFn: async () => {
      const r = await authFetch(`/api/partenaires/actualites/${editing!.id}/bundle`);
      const rows = await r.json();
      return rows.map((row: any) => ({
        rowId: row.id,
        productId: row.productId,
        nomFr: row.nomFr || "",
        sku: row.sku || "",
        imageUrl: row.imageUrl || null,
        quantite: row.quantite || 1,
        prixUnitaire: row.prixUnitaire || "",
        offert: row.offert || false,
      }));
    },
    enabled: !!editing?.id && isBundle,
  });

  // Initialise localBundle depuis le serveur (une seule fois à l'ouverture)
  useEffect(() => {
    if (serverBundleItems.length > 0 && localBundle.length === 0) {
      setLocalBundle(serverBundleItems);
    }
  }, [serverBundleItems]);

  const filteredProducts = allProducts.filter(p =>
    p.nomFr.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.sku.toLowerCase().includes(productSearch.toLowerCase())
  );

  const handleImg = async (f: File) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("image", f);
      const r = await authFetch("/api/partenaires/actualites/upload-image", { method: "POST", body: fd });
      if (!r.ok) throw new Error();
      const { url } = await r.json(); setImageUrl(url);
    } catch { toast({ title: "Erreur upload image", variant: "destructive" }); }
    finally { setUploading(false); }
  };

  const handlePdf = async (f: File, actuId: string) => {
    setUploadingPdf(true);
    try {
      const fd = new FormData(); fd.append("pdf", f);
      const r = await authFetch(`/api/partenaires/actualites/${actuId}/upload-pdf`, { method: "POST", body: fd });
      if (!r.ok) throw new Error();
      const { url } = await r.json(); setDocumentPdfUrl(url);
    } catch { toast({ title: "Erreur upload PDF", variant: "destructive" }); }
    finally { setUploadingPdf(false); }
  };

  const addBundleProduct = (product: ProductItem) => {
    if (localBundle.some(i => i.productId === product.id)) {
      toast({ title: "Ce produit est déjà dans le bundle" }); return;
    }
    setLocalBundle(prev => [...prev, {
      productId: product.id, nomFr: product.nomFr, sku: product.sku,
      imageUrl: product.imageUrl || null,
      quantite: 1, prixUnitaire: product.prixHt || "", offert: false,
    }]);
    setProductSearch("");
  };

  const removeBundleItem = (idx: number) => {
    setLocalBundle(prev => prev.filter((_, i) => i !== idx));
  };

  const updateBundleItem = (idx: number, field: keyof LocalBundleItem, value: any) => {
    setLocalBundle(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const moveBundleItem = (idx: number, dir: "up" | "down") => {
    setLocalBundle(prev => {
      const next = [...prev];
      const to = dir === "up" ? idx - 1 : idx + 1;
      if (to < 0 || to >= next.length) return prev;
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  };

  // Calcul récap bundle
  const bundleSummary = localBundle.reduce((acc, item) => {
    const prix = parseFloat(item.prixUnitaire) || 0;
    const qty = item.quantite || 1;
    if (item.offert) acc.offert += prix * qty;
    else acc.payant += prix * qty;
    return acc;
  }, { payant: 0, offert: 0 });

  const save = async () => {
    if (!titre.trim()) return;
    setSaving(true);
    try {
      const url = editing ? `/api/partenaires/actualites/${editing.id}` : "/api/partenaires/actualites";
      const res = await authFetch(url, {
        method: editing ? "PUT" : "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          titre, accroche, contenu, type, visibilite, actif,
          prixPromo: prixPromo || null, prixNormal: prixNormal || null,
          dateDebut: dateDebut || null, dateFin: dateFin || null,
          documentId: documentId || null, imageUrl,
          isBundle: type === "promotion" && isBundle,
          documentPdfUrl: documentPdfUrl || null,
        }),
      });
      if (!res.ok) { toast({ title: "Erreur", variant: "destructive" }); return; }
      const saved = await res.json();
      const actuId = saved.id;

      // Upload PDF si fichier sélectionné
      if (pdfRef.current?.files?.[0] && actuId) {
        await handlePdf(pdfRef.current.files[0], actuId);
      }

      // Sync bundle items (delete all + re-insert)
      if (type === "promotion" && isBundle && actuId) {
        // Supprimer les items existants via les rowIds qu'on connaît
        const existingIds = localBundle.filter(i => i.rowId).map(i => i.rowId!);
        // Pour simplifier : supprimer tous les items server et re-créer
        const existingOnServer = serverBundleItems.map(i => i.rowId);
        await Promise.all(existingOnServer.map(rowId =>
          authFetch(`/api/partenaires/actualites/${actuId}/bundle/${rowId}`, { method: "DELETE" })
        ));
        // Insérer les items locaux dans l'ordre
        for (let i = 0; i < localBundle.length; i++) {
          const item = localBundle[i];
          await authFetch(`/api/partenaires/actualites/${actuId}/bundle`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              productId: item.productId, quantite: item.quantite,
              prixUnitaire: item.prixUnitaire || null, offert: item.offert, ordre: i,
            }),
          });
        }
      }

      qc.invalidateQueries({ queryKey: ["/api/partenaires/actualites"] });
      onSaved(); onClose();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{editing ? "Modifier l'actualité" : "Nouvelle actualité"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 max-h-[72vh] overflow-y-auto pr-2">

          {/* Titre + Type */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Titre *</Label><Input value={titre} onChange={e => setTitre(e.target.value)} /></div>
            <div><Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="information">Information</SelectItem>
                  <SelectItem value="nouveaute">Nouveauté</SelectItem>
                  <SelectItem value="promotion">Promotion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Visibilité</Label>
              <Select value={visibilite} onValueChange={setVisibilite}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tous">Tous</SelectItem>
                  <SelectItem value="revendeur_premium">Premium</SelectItem>
                  <SelectItem value="sous_distributeur">Sous-distributeurs</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div><Label>Accroche</Label><Textarea value={accroche} onChange={e => setAccroche(e.target.value)} rows={2} /></div>
          <div><Label>Contenu</Label><Textarea value={contenu} onChange={e => setContenu(e.target.value)} rows={3} /></div>

          {/* Champs promotion */}
          {type === "promotion" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prix promo (€)</Label><Input value={prixPromo} onChange={e => setPrixPromo(e.target.value)} /></div>
              <div><Label>Prix normal (€)</Label><Input value={prixNormal} onChange={e => setPrixNormal(e.target.value)} /></div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Début</Label><Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} /></div>
            <div><Label>Fin</Label><Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} /></div>
          </div>

          <div><Label>Document lié</Label>
            <Select value={documentId || "none"} onValueChange={v => setDocumentId(v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {documents.map(d => <SelectItem key={d.id} value={d.id}>{d.titre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Image */}
          <div>
            <Label>Image de couverture</Label>
            <div className="flex gap-2 mt-1 items-center">
              <Button type="button" variant="outline" size="sm" onClick={() => imgRef.current?.click()} disabled={uploading} className="gap-2">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Image
              </Button>
              {imageUrl && <img src={imageUrl} alt="" className="h-10 w-10 rounded object-cover" />}
            </div>
            <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleImg(f); }} />
          </div>

          {/* ─── SECTION BUNDLE ──────────────────────────────────────────── */}
          {type === "promotion" && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/20">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" htmlFor="bundle-toggle">
                  <Tag className="w-4 h-4 text-orange-500" />
                  Offre bundle (plusieurs produits)
                </Label>
                <Switch id="bundle-toggle" checked={isBundle} onCheckedChange={v => { setIsBundle(v); if (!v) setLocalBundle([]); }} />
              </div>

              {isBundle && (
                <div className="space-y-4">
                  {/* Liste des produits du bundle */}
                  {localBundle.length > 0 && (
                    <div className="space-y-2">
                      {localBundle.map((item, idx) => (
                        <div key={`${item.productId}-${idx}`} className="flex gap-2 items-start bg-background border rounded-lg p-3">
                          {/* Miniature */}
                          <div className="w-10 h-10 rounded bg-muted/50 overflow-hidden flex-shrink-0">
                            {item.imageUrl
                              ? <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
                              : <Package className="w-6 h-6 m-2 text-muted-foreground" />}
                          </div>
                          {/* Infos produit */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{item.nomFr}</p>
                            <p className="text-xs text-muted-foreground">{item.sku}</p>
                            <div className="flex gap-2 mt-2 items-center flex-wrap">
                              <div className="flex items-center gap-1">
                                <Label className="text-xs">Qté</Label>
                                <Input type="number" min={1} value={item.quantite}
                                  onChange={e => updateBundleItem(idx, "quantite", parseInt(e.target.value) || 1)}
                                  className="w-16 h-7 text-xs" />
                              </div>
                              <div className="flex items-center gap-1">
                                <Label className="text-xs">Prix U. (€)</Label>
                                <Input value={item.prixUnitaire}
                                  onChange={e => updateBundleItem(idx, "prixUnitaire", e.target.value)}
                                  className="w-20 h-7 text-xs" placeholder="0.00"
                                  disabled={item.offert} />
                              </div>
                              <label className="flex items-center gap-1 text-xs cursor-pointer select-none">
                                <Switch checked={item.offert} onCheckedChange={v => updateBundleItem(idx, "offert", v)}
                                  className="scale-75" />
                                <Gift className="w-3 h-3 text-green-600" />
                                Offert
                              </label>
                              {item.offert && (
                                <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Offert</span>
                              )}
                            </div>
                          </div>
                          {/* Actions */}
                          <div className="flex flex-col gap-1 ml-1">
                            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => moveBundleItem(idx, "up")} disabled={idx === 0}>
                              <ArrowUp className="w-3 h-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => moveBundleItem(idx, "down")} disabled={idx === localBundle.length - 1}>
                              <ArrowDown className="w-3 h-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive hover:text-destructive" onClick={() => removeBundleItem(idx)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Récapitulatif bundle */}
                  {localBundle.length > 0 && (
                    <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200/60 rounded-lg p-3 text-xs space-y-1">
                      <p className="font-semibold text-sm flex items-center gap-1.5"><Tag className="w-3.5 h-3.5 text-orange-500" />Récapitulatif bundle</p>
                      <div className="flex justify-between"><span>Produits payants :</span><span className="font-medium">{bundleSummary.payant.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</span></div>
                      {bundleSummary.offert > 0 && <div className="flex justify-between text-green-700"><span>Valeur offerte :</span><span className="font-medium">-{bundleSummary.offert.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</span></div>}
                      <div className="flex justify-between font-bold border-t pt-1 mt-1"><span>Total bundle :</span><span>{bundleSummary.payant.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</span></div>
                    </div>
                  )}

                  {/* Sélecteur produit */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Ajouter un produit au bundle</Label>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Rechercher par nom ou SKU…"
                        value={productSearch}
                        onChange={e => setProductSearch(e.target.value)}
                        className="pl-8 h-8 text-xs"
                      />
                    </div>
                    {productSearch.length >= 1 && (
                      <div className="border rounded-lg max-h-40 overflow-y-auto bg-background shadow-sm">
                        {filteredProducts.length === 0 && (
                          <p className="text-xs text-muted-foreground p-3">Aucun produit trouvé</p>
                        )}
                        {filteredProducts.slice(0, 8).map(p => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => addBundleProduct(p)}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 text-left text-xs border-b last:border-0"
                            data-testid={`bundle-pick-${p.id}`}
                          >
                            <div className="w-7 h-7 rounded bg-muted overflow-hidden flex-shrink-0">
                              {p.imageUrl ? <img src={p.imageUrl} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 m-1.5 text-muted-foreground" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{p.nomFr}</p>
                              <p className="text-muted-foreground">{p.sku}</p>
                            </div>
                            <Plus className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* PDF détail de l'offre */}
                  <div>
                    <Label className="text-xs">PDF détail de l'offre (optionnel)</Label>
                    <div className="flex gap-2 mt-1 items-center flex-wrap">
                      <Button type="button" variant="outline" size="sm" onClick={() => pdfRef.current?.click()} disabled={uploadingPdf} className="gap-2 text-xs h-8">
                        {uploadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {documentPdfUrl ? "Remplacer le PDF" : "Téléverser un PDF"}
                      </Button>
                      {documentPdfUrl && (
                        <a href={documentPdfUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline">
                          <FileText className="w-3.5 h-3.5" />PDF actuel
                        </a>
                      )}
                    </div>
                    <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2"><Switch id="actif-a" checked={actif} onCheckedChange={setActif} /><Label htmlFor="actif-a">Actif</Label></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Document Explorer ──────────────────────────────────────────────────────────
function DocumentExplorer({ resellers }: { resellers: Reseller[] }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inlineCreateRef = useRef<HTMLInputElement>(null);
  const inlineRenameRef = useRef<HTMLInputElement>(null);

  // Navigation
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([{ id: null, nom: "Espace partenaires" }]);

  // UI
  const [viewMode, setViewMode] = useState<"grid" | "list">(() =>
    (localStorage.getItem("partenaires-view") as "grid" | "list") || "grid");
  const [treeOpen, setTreeOpen] = useState(() => localStorage.getItem("partenaires-tree") === "true");
  const [searchQuery, setSearchQuery] = useState("");

  // Selection (key = "folder:id" | "doc:id")
  const [selectedKeys, setSelectedKeys] = useState<Set<SelectionKey>>(new Set());

  // Context menu
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: ContextItem } | null>(null);

  // Inline
  const [inlineCreate, setInlineCreate] = useState(false);
  const [inlineCreateName, setInlineCreateName] = useState("Nouveau dossier");
  const [inlineRename, setInlineRename] = useState<{ type: "folder" | "document"; id: string; name: string } | null>(null);

  // Upload queue
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const uploadBusyRef = useRef(false);

  // Drag state
  const [globalDragOver, setGlobalDragOver] = useState(false);
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dropTargetFolderId, setDropTargetFolderId] = useState<string | null>(null);

  // Dialogs
  const [docModalData, setDocModalData] = useState<{ editing: Document | null } | null>(null);
  const [moveDialogItems, setMoveDialogItems] = useState<{ id: string; type: "folder" | "document"; nom: string }[] | null>(null);
  const [visibilityDialogIds, setVisibilityDialogIds] = useState<string[] | null>(null);
  const [lecturesDocId, setLecturesDocId] = useState<string | null>(null);
  const [videoPreview, setVideoPreview] = useState<{ doc: Document; url: string } | null>(null);

  // Data queries
  const { data: allCategories = [] } = useQuery<Categorie[]>({
    queryKey: ["/api/partenaires/categories"],
    queryFn: async () => { const r = await authFetch("/api/partenaires/categories"); return r.json(); },
  });

  const { data: content, isLoading: contentLoading } = useQuery<{ folders: Categorie[]; documents: Document[] }>({
    queryKey: ["/api/partenaires", "contenu", currentFolderId || "root"],
    queryFn: async () => {
      const r = await authFetch(`/api/partenaires/categories/${currentFolderId || "root"}/contenu`);
      return r.json();
    },
  });

  const { data: allDocuments = [] } = useQuery<Document[]>({
    queryKey: ["/api/partenaires/documents"],
    queryFn: async () => { const r = await authFetch("/api/partenaires/documents"); return r.json(); },
    enabled: searchQuery.length > 0,
  });

  // Persist view mode
  useEffect(() => { localStorage.setItem("partenaires-view", viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem("partenaires-tree", String(treeOpen)); }, [treeOpen]);

  // Global drag-over for file drops
  useEffect(() => {
    const over = (e: DragEvent) => { if (e.dataTransfer?.types.includes("Files")) { e.preventDefault(); setGlobalDragOver(true); } };
    const leave = (e: DragEvent) => { if (!e.relatedTarget) setGlobalDragOver(false); };
    const drop = (e: DragEvent) => { e.preventDefault(); setGlobalDragOver(false); if (e.dataTransfer?.files.length) startUpload(Array.from(e.dataTransfer.files)); };
    document.addEventListener("dragover", over);
    document.addEventListener("dragleave", leave);
    document.addEventListener("drop", drop);
    return () => { document.removeEventListener("dragover", over); document.removeEventListener("dragleave", leave); document.removeEventListener("drop", drop); };
  }, [currentFolderId]);

  // Focus inline inputs
  useEffect(() => { if (inlineCreate && inlineCreateRef.current) { inlineCreateRef.current.focus(); inlineCreateRef.current.select(); } }, [inlineCreate]);
  useEffect(() => { if (inlineRename && inlineRenameRef.current) { inlineRenameRef.current.focus(); inlineRenameRef.current.select(); } }, [inlineRename]);

  // Navigate
  const navigateTo = useCallback((id: string | null) => {
    setCurrentFolderId(id);
    setBreadcrumb(buildBreadcrumb(allCategories, id));
    setSelectedKeys(new Set());
    setContextMenu(null);
    setInlineCreate(false);
    setInlineRename(null);
  }, [allCategories]);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    const matchedFolders = allCategories.filter(c => c.nom.toLowerCase().includes(q)).map(c => {
      const path = buildBreadcrumb(allCategories, c.parentId).map(b => b.nom).join(" > ");
      return { ...c, _path: path };
    });
    const matchedDocs = allDocuments.filter(d => d.titre.toLowerCase().includes(q)).map(d => {
      const path = d.categorieId ? buildBreadcrumb(allCategories, d.categorieId).map(b => b.nom).join(" > ") : "Espace partenaires";
      return { ...d, _path: path };
    });
    return { folders: matchedFolders, documents: matchedDocs };
  }, [searchQuery, allCategories, allDocuments]);

  // Upload handler
  const startUpload = useCallback(async (files: File[]) => {
    if (uploadBusyRef.current) return;
    uploadBusyRef.current = true;
    const entries: UploadEntry[] = files.map(f => ({ name: f.name, progress: 0, done: false }));
    setUploads(entries);
    let successes = 0;
    let failures = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const r = await uploadWithProgress(files[i], p => setUploads(prev => prev.map((e, idx) => idx === i ? { ...e, progress: p } : e)));
        const ext = files[i].name.split(".").pop()?.toLowerCase() || "";
        const metadata = { titre: files[i].name.replace(/\.[^/.]+$/, ""), categorieId: currentFolderId || null, fichierUrl: r.path, typeFichier: detectType(r.mimetype, ext), tailleFichier: r.size, visibilite: "tous", notifier: false, actif: true };
        if (r.receipt) {
          await confirmPartenaireVideo(r as VideoUpload, metadata);
        } else {
          const response = await authFetch("/api/partenaires/documents", {
            method: "POST", body: JSON.stringify(metadata),
          });
          if (!response.ok) { const error = await response.json(); throw new Error(error.message || "Enregistrement impossible"); }
        }
        successes++;
        setUploads(prev => prev.map((e, idx) => idx === i ? { ...e, progress: 100, done: true } : e));
      } catch (err: any) {
        failures++;
        console.error("[partenaires] ajout", files[i].name, err);
        setUploads(prev => prev.map((e, idx) => idx === i ? { ...e, error: err.message, done: true } : e));
        toast({ title: err.message || "Envoi impossible", variant: "destructive" });
      }
    }
    uploadBusyRef.current = false;
    invalidateDocumentCaches(qc);
    if (successes > 0) toast({ title: `${successes} fichier(s) importé(s)` });
    if (!failures) setTimeout(() => { if (!uploadBusyRef.current) setUploads([]); }, 3000);
  }, [currentFolderId, qc, toast]);

  // Inline folder create
  const commitCreate = async () => {
    const nom = inlineCreateName.trim() || "Nouveau dossier";
    await authFetch("/api/partenaires/categories", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nom, parentId: currentFolderId || null, ordre: 999 }),
    });
    qc.invalidateQueries({ queryKey: ["/api/partenaires"] });
    setInlineCreate(false);
    setInlineCreateName("Nouveau dossier");
    toast({ title: `Dossier "${nom}" créé` });
  };

  // Inline rename
  const commitRename = async () => {
    if (!inlineRename) return;
    const nom = inlineRename.name.trim();
    if (!nom) { setInlineRename(null); return; }
    if (inlineRename.type === "folder") {
      await authFetch(`/api/partenaires/categories/${inlineRename.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nom }),
      });
    } else {
      const doc = content?.documents.find(d => d.id === inlineRename.id);
      if (doc) {
        await authFetch(`/api/partenaires/documents/${inlineRename.id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...doc, titre: nom }),
        });
      }
    }
    qc.invalidateQueries({ queryKey: ["/api/partenaires"] });
    setInlineRename(null);
  };

  // Delete
  const deleteItem = async (item: ContextItem) => {
    if (!confirm(`Supprimer "${item.nom}" ?`)) return;
    if (item.type === "folder") {
      const res = await authFetch(`/api/partenaires/categories/${item.id}`, { method: "DELETE" });
      if (!res.ok) { const d = await res.json(); toast({ title: d.message || "Impossible de supprimer", variant: "destructive" }); return; }
    } else {
      const doc = item.data as Document;
      const endpoint = doc.storageBucket === "partenaire-medias" ? "videos" : "documents";
      const response = await authFetch(`/api/partenaires/${endpoint}/${item.id}`, { method: "DELETE" });
      if (!response.ok) { const error = await response.json(); toast({ title: error.message || "Suppression impossible", variant: "destructive" }); return; }
    }
    invalidateDocumentCaches(qc);
    setSelectedKeys(prev => { const n = new Set(prev); n.delete(item.type === "folder" ? `folder:${item.id}` : `doc:${item.id}`); return n; });
  };

  // Download
  const downloadDoc = async (doc: Document) => {
    try {
      const res = await authFetch(`/api/partenaires/documents/${doc.id}/download`);
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.message || "Téléchargement impossible");
      if (doc.mimeType?.startsWith("video/")) {
        setVideoPreview({ doc, url: data.url });
      } else {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("[partenaires] téléchargement", error);
      toast({ title: error.message || "Téléchargement impossible", variant: "destructive" });
    }
  };

  // Toggle select
  const toggleSelect = (key: SelectionKey, multi: boolean) => {
    setSelectedKeys(prev => {
      if (multi) {
        const n = new Set(prev);
        if (n.has(key)) n.delete(key); else n.add(key);
        return n;
      } else {
        if (prev.size === 1 && prev.has(key)) return new Set();
        return new Set([key]);
      }
    });
  };

  // Context menu handler
  const openContextMenu = (e: React.MouseEvent, item: ContextItem) => {
    e.preventDefault(); e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, item });
  };

  // Build selected items list for bulk actions
  const selectedItems = useMemo(() => {
    const items: { id: string; type: "folder" | "document"; nom: string }[] = [];
    for (const key of selectedKeys) {
      if (key.startsWith("folder:")) {
        const id = key.slice(7);
        const f = content?.folders.find(f => f.id === id);
        if (f) items.push({ id, type: "folder", nom: f.nom });
      } else if (key.startsWith("doc:")) {
        const id = key.slice(4);
        const d = content?.documents.find(d => d.id === id);
        if (d) items.push({ id, type: "document", nom: d.titre });
      }
    }
    return items;
  }, [selectedKeys, content]);

  // DnD folder drop
  const handleDropOnFolder = async (targetFolderId: string | null) => {
    if (!draggedKey) return;
    const type = draggedKey.startsWith("folder:") ? "category" : "document";
    const id = draggedKey.startsWith("folder:") ? draggedKey.slice(7) : draggedKey.slice(4);
    await authFetch("/api/partenaires/documents/batch-move", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: [id], type: type === "category" ? "category" : "document", target_category_id: targetFolderId }),
    });
    qc.invalidateQueries({ queryKey: ["/api/partenaires"] });
    toast({ title: "Élément déplacé" });
    setDraggedKey(null);
    setDropTargetFolderId(null);
  };

  const displayFolders = searchResults ? searchResults.folders : (content?.folders || []);
  const displayDocuments = searchResults ? searchResults.documents : (content?.documents || []);

  // ── Grid / List item renderer ─────────────────────────────────────────────
  const renderGridFolder = (cat: Categorie & { _path?: string }) => {
    const key = `folder:${cat.id}`;
    const isSelected = selectedKeys.has(key);
    const isDropTarget = dropTargetFolderId === cat.id;
    const isRenaming = inlineRename?.type === "folder" && inlineRename.id === cat.id;

    return (
      <div
        key={cat.id}
        draggable
        onDragStart={() => setDraggedKey(key)}
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDropTargetFolderId(cat.id); }}
        onDragLeave={() => setDropTargetFolderId(null)}
        onDrop={e => { e.preventDefault(); e.stopPropagation(); handleDropOnFolder(cat.id); }}
        onDragEnd={() => { setDraggedKey(null); setDropTargetFolderId(null); }}
        onClick={e => { e.stopPropagation(); toggleSelect(key, e.ctrlKey || e.metaKey); }}
        onDoubleClick={() => !isRenaming && navigateTo(cat.id)}
        onContextMenu={e => openContextMenu(e, { id: cat.id, type: "folder", nom: cat.nom, data: cat })}
        className={[
          "relative flex flex-col items-center gap-2 p-3 rounded-xl border cursor-pointer select-none transition-all group",
          isSelected ? "bg-primary/10 border-primary/40" : "bg-card border-transparent hover:bg-muted/60 hover:border-border",
          isDropTarget ? "outline-2 outline outline-blue-400 bg-blue-50 dark:bg-blue-950/20" : "",
          draggedKey === key ? "opacity-40" : "",
        ].filter(Boolean).join(" ")}
        data-testid={`grid-folder-${cat.id}`}
      >
        <Folder className="w-16 h-16 text-amber-400 shrink-0" />
        {isRenaming ? (
          <input
            ref={inlineRenameRef}
            value={inlineRename!.name}
            onChange={e => setInlineRename(r => r ? { ...r, name: e.target.value } : r)}
            onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setInlineRename(null); }}
            onBlur={commitRename}
            onClick={e => e.stopPropagation()}
            className="w-full text-center text-xs font-medium rounded border border-primary px-1 py-0.5 outline-none"
          />
        ) : (
          <span className="text-xs font-medium text-center line-clamp-2 w-full">{cat.nom}</span>
        )}
        <span className="text-[10px] text-muted-foreground">
          {cat._path ? <span className="italic">{cat._path}</span>
            : `${cat.subfolders || 0} doss., ${cat.subdocs || 0} fich.`}
        </span>
      </div>
    );
  };

  const renderGridFile = (doc: Document & { _path?: string }) => {
    const key = `doc:${doc.id}`;
    const isSelected = selectedKeys.has(key);
    const isRenaming = inlineRename?.type === "document" && inlineRename.id === doc.id;

    return (
      <div
        key={doc.id}
        draggable
        onDragStart={() => setDraggedKey(key)}
        onDragEnd={() => setDraggedKey(null)}
        onClick={e => { e.stopPropagation(); toggleSelect(key, e.ctrlKey || e.metaKey); }}
        onDoubleClick={() => !isRenaming && downloadDoc(doc)}
        onContextMenu={e => openContextMenu(e, { id: doc.id, type: "document", nom: doc.titre, data: doc })}
        className={[
          "relative flex flex-col items-center gap-2 p-3 rounded-xl border cursor-pointer select-none transition-all group",
          isSelected ? "bg-primary/10 border-primary/40" : "bg-card border-transparent hover:bg-muted/60 hover:border-border",
          draggedKey === key ? "opacity-40" : "",
        ].filter(Boolean).join(" ")}
        data-testid={`grid-doc-${doc.id}`}
      >
        <TypeIcon type={doc.typeFichier} size={56} />
        {isRenaming ? (
          <input
            ref={inlineRenameRef}
            value={inlineRename!.name}
            onChange={e => setInlineRename(r => r ? { ...r, name: e.target.value } : r)}
            onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setInlineRename(null); }}
            onBlur={commitRename}
            onClick={e => e.stopPropagation()}
            className="w-full text-center text-xs font-medium rounded border border-primary px-1 py-0.5 outline-none"
          />
        ) : (
          <span className="text-xs font-medium text-center line-clamp-2 w-full">{doc.titre}</span>
        )}
        <div className="flex items-center gap-1.5 flex-wrap justify-center">
          {doc._path ? <span className="text-[10px] italic text-muted-foreground">{doc._path}</span>
            : <><span className="text-[10px] text-muted-foreground">{formatBytes(doc.tailleFichier)}</span><VisiBadge v={doc.visibilite} /></>}
        </div>
        {!doc.actif && <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-muted-foreground/30" title="Inactif" />}
      </div>
    );
  };

  // ── Inline new folder card ─────────────────────────────────────────────────
  const renderInlineCreate = () => (
    <div className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 cursor-default select-none">
      <Folder className="w-16 h-16 text-amber-300 shrink-0" />
      <input
        ref={inlineCreateRef}
        value={inlineCreateName}
        onChange={e => setInlineCreateName(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") commitCreate(); if (e.key === "Escape") { setInlineCreate(false); setInlineCreateName("Nouveau dossier"); } }}
        onBlur={commitCreate}
        onClick={e => e.stopPropagation()}
        className="w-full text-center text-xs font-medium rounded border border-primary px-1 py-0.5 outline-none bg-card"
      />
    </div>
  );

  // ── Empty state ────────────────────────────────────────────────────────────
  const isEmpty = !contentLoading && displayFolders.length === 0 && displayDocuments.length === 0 && !inlineCreate && !searchQuery;

  return (
    <div className="flex flex-col h-[calc(100vh-170px)]" onClick={() => { setSelectedKeys(new Set()); setContextMenu(null); }}>

      {/* Tree Drawer */}
      <TreeDrawer open={treeOpen} onClose={() => setTreeOpen(false)} categories={allCategories} currentFolderId={currentFolderId} onNavigate={id => { navigateTo(id); setTreeOpen(false); }} />

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" onClick={e => e.stopPropagation()}>
        <Button size="sm" variant="outline" onClick={() => setInlineCreate(true)} className="gap-1.5" data-testid="btn-new-folder">
          <Folder className="w-4 h-4 text-amber-500" />Nouveau dossier
        </Button>
        <Button size="sm" variant="outline" disabled={uploads.some(entry => !entry.done)} onClick={() => fileInputRef.current?.click()} className="gap-1.5" data-testid="btn-upload">
          <Upload className="w-4 h-4" />Uploader
        </Button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => { if (e.target.files?.length) startUpload(Array.from(e.target.files)); }} />

        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Rechercher…"
            className="pl-8 h-8 text-sm"
            data-testid="input-search"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2">
              <X className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 ml-auto">
          <Button size="icon" variant={viewMode === "grid" ? "secondary" : "ghost"} className="h-8 w-8" onClick={() => setViewMode("grid")} data-testid="btn-view-grid">
            <Grid3X3 className="w-4 h-4" />
          </Button>
          <Button size="icon" variant={viewMode === "list" ? "secondary" : "ghost"} className="h-8 w-8" onClick={() => setViewMode("list")} data-testid="btn-view-list">
            <List className="w-4 h-4" />
          </Button>
          <Button size="icon" variant={treeOpen ? "secondary" : "ghost"} className="h-8 w-8" onClick={() => setTreeOpen(o => !o)} data-testid="btn-tree">
            <TreePine className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Breadcrumb */}
      {!searchQuery && (
        <div className="flex items-center gap-1 text-sm mb-3 flex-wrap" onClick={e => e.stopPropagation()}>
          {breadcrumb.map((item, i) => (
            <div key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
              {i === 0
                ? <button onClick={() => navigateTo(null)} className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
                  <Home className="w-3.5 h-3.5" />
                  {breadcrumb.length === 1 && <span>Espace partenaires</span>}
                </button>
                : i < breadcrumb.length - 1
                  ? <button onClick={() => navigateTo(item.id)} className="text-muted-foreground hover:text-foreground transition-colors">{item.nom}</button>
                  : <span className="font-medium">{item.nom}</span>
              }
            </div>
          ))}
        </div>
      )}

      {/* Search label */}
      {searchQuery && (
        <div className="text-sm text-muted-foreground mb-3">
          {(displayFolders.length + displayDocuments.length)} résultat(s) pour « {searchQuery} »
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 overflow-y-auto">
        {contentLoading && !searchQuery ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
            <Folder className="w-20 h-20 text-muted-foreground/20" />
            {!currentFolderId ? (
              <>
                <p className="text-lg font-semibold text-muted-foreground">Bienvenue dans l'espace partenaires</p>
                <p className="text-sm text-muted-foreground">Créez vos premiers dossiers pour organiser vos documents</p>
                <Button onClick={() => setInlineCreate(true)} className="gap-2 mt-2">
                  <Folder className="w-4 h-4" />Créer un dossier
                </Button>
              </>
            ) : (
              <>
                <p className="text-lg font-semibold text-muted-foreground">Ce dossier est vide</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setInlineCreate(true)} className="gap-2"><Folder className="w-4 h-4" />Nouveau dossier</Button>
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2"><Upload className="w-4 h-4" />Uploader</Button>
                </div>
              </>
            )}
          </div>
        ) : viewMode === "grid" ? (
          /* GRID VIEW */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 pb-24">
            {inlineCreate && renderInlineCreate()}
            {displayFolders.map(cat => renderGridFolder(cat as any))}
            {displayDocuments.map(doc => renderGridFile(doc as any))}
          </div>
        ) : (
          /* LIST VIEW */
          <div className="border rounded-lg overflow-hidden pb-24">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/30 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-3 py-2 w-8" />
                  <th className="text-left px-3 py-2 font-medium">Nom</th>
                  <th className="text-left px-3 py-2 font-medium">Type</th>
                  <th className="text-left px-3 py-2 font-medium">Visibilité</th>
                  <th className="text-left px-3 py-2 font-medium">Taille</th>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Lu</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {inlineCreate && (
                  <tr className="border-b bg-primary/5">
                    <td className="px-3 py-2"><Folder className="w-5 h-5 text-amber-400" /></td>
                    <td colSpan={7} className="px-3 py-2">
                      <input ref={inlineCreateRef} value={inlineCreateName} onChange={e => setInlineCreateName(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") commitCreate(); if (e.key === "Escape") { setInlineCreate(false); setInlineCreateName("Nouveau dossier"); } }}
                        onBlur={commitCreate} className="text-sm font-medium rounded border border-primary px-2 py-0.5 outline-none w-48" />
                    </td>
                  </tr>
                )}
                {displayFolders.map(cat => {
                  const key = `folder:${cat.id}`;
                  const isSelected = selectedKeys.has(key);
                  const isRenaming = inlineRename?.type === "folder" && inlineRename.id === cat.id;
                  return (
                    <tr key={cat.id}
                      draggable onDragStart={() => setDraggedKey(key)} onDragEnd={() => setDraggedKey(null)}
                      onDragOver={e => { e.preventDefault(); setDropTargetFolderId(cat.id); }}
                      onDragLeave={() => setDropTargetFolderId(null)}
                      onDrop={e => { e.preventDefault(); handleDropOnFolder(cat.id); }}
                      onClick={e => { e.stopPropagation(); toggleSelect(key, e.ctrlKey || e.metaKey); }}
                      onDoubleClick={() => !isRenaming && navigateTo(cat.id)}
                      onContextMenu={e => openContextMenu(e, { id: cat.id, type: "folder", nom: cat.nom, data: cat })}
                      className={`border-b transition-colors cursor-pointer group ${isSelected ? "bg-primary/8" : "hover:bg-muted/30"} ${dropTargetFolderId === cat.id ? "outline-2 outline outline-blue-400" : ""}`}
                      data-testid={`row-folder-${cat.id}`}
                    >
                      <td className="px-3 py-2"><Folder className="w-5 h-5 text-amber-400" /></td>
                      <td className="px-3 py-2 font-medium">
                        {isRenaming ? (
                          <input ref={inlineRenameRef} value={inlineRename!.name}
                            onChange={e => setInlineRename(r => r ? { ...r, name: e.target.value } : r)}
                            onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setInlineRename(null); }}
                            onBlur={commitRename} onClick={e => e.stopPropagation()}
                            className="text-sm font-medium rounded border border-primary px-2 py-0.5 outline-none" />
                        ) : cat.nom}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">Dossier</td>
                      <td className="px-3 py-2" />
                      <td className="px-3 py-2 text-xs text-muted-foreground">{(cat as any)._path ? (cat as any)._path : `${cat.subfolders || 0} doss., ${cat.subdocs || 0} fich.`}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{formatDate(cat.createdAt)}</td>
                      <td className="px-3 py-2" />
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                          <button onClick={e => { e.stopPropagation(); setInlineRename({ type: "folder", id: cat.id, name: cat.nom }); }} className="p-1 rounded hover:bg-muted"><Pencil className="w-3.5 h-3.5 text-muted-foreground" /></button>
                          <button onClick={e => { e.stopPropagation(); setMoveDialogItems([{ id: cat.id, type: "folder", nom: cat.nom }]); }} className="p-1 rounded hover:bg-muted"><MoveRight className="w-3.5 h-3.5 text-muted-foreground" /></button>
                          <button onClick={e => { e.stopPropagation(); deleteItem({ id: cat.id, type: "folder", nom: cat.nom, data: cat }); }} className="p-1 rounded hover:bg-muted"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {displayDocuments.map(doc => {
                  const key = `doc:${doc.id}`;
                  const isSelected = selectedKeys.has(key);
                  const isRenaming = inlineRename?.type === "document" && inlineRename.id === doc.id;
                  return (
                    <tr key={doc.id}
                      draggable onDragStart={() => setDraggedKey(key)} onDragEnd={() => setDraggedKey(null)}
                      onClick={e => { e.stopPropagation(); toggleSelect(key, e.ctrlKey || e.metaKey); }}
                      onDoubleClick={() => !isRenaming && downloadDoc(doc)}
                      onContextMenu={e => openContextMenu(e, { id: doc.id, type: "document", nom: doc.titre, data: doc })}
                      className={`border-b transition-colors cursor-pointer group ${isSelected ? "bg-primary/8" : "hover:bg-muted/30"}`}
                      data-testid={`row-doc-${doc.id}`}
                    >
                      <td className="px-3 py-2"><TypeIcon type={doc.typeFichier} size={18} /></td>
                      <td className="px-3 py-2 font-medium">
                        {isRenaming ? (
                          <input ref={inlineRenameRef} value={inlineRename!.name}
                            onChange={e => setInlineRename(r => r ? { ...r, name: e.target.value } : r)}
                            onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setInlineRename(null); }}
                            onBlur={commitRename} onClick={e => e.stopPropagation()}
                            className="text-sm rounded border border-primary px-2 py-0.5 outline-none" />
                        ) : <span className="truncate max-w-[220px] block">{doc.titre}</span>}
                        {(doc as any)._path && <span className="text-[10px] text-muted-foreground italic">{(doc as any)._path}</span>}
                      </td>
                      <td className="px-3 py-2">{doc.typeFichier && <span className="text-xs capitalize">{doc.typeFichier}</span>}</td>
                      <td className="px-3 py-2"><VisiBadge v={doc.visibilite} /></td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{formatBytes(doc.tailleFichier)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{formatDate(doc.createdAt)}</td>
                      <td className="px-3 py-2">
                        <button onClick={e => { e.stopPropagation(); setLecturesDocId(doc.id); }} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"><Eye className="w-3 h-3" />{doc.nbLectures}</button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                          <button onClick={e => { e.stopPropagation(); downloadDoc(doc); }} title={doc.mimeType?.startsWith("video/") ? "Lire la vidéo" : "Télécharger"} data-testid={`btn-lire-${doc.id}`} className="p-1 rounded hover:bg-muted">{doc.mimeType?.startsWith("video/") ? <Film className="w-3.5 h-3.5 text-muted-foreground" /> : <Download className="w-3.5 h-3.5 text-muted-foreground" />}</button>
                          <button onClick={e => { e.stopPropagation(); setInlineRename({ type: "document", id: doc.id, name: doc.titre }); }} className="p-1 rounded hover:bg-muted"><Pencil className="w-3.5 h-3.5 text-muted-foreground" /></button>
                          <button onClick={e => { e.stopPropagation(); setDocModalData({ editing: doc }); }} className="p-1 rounded hover:bg-muted"><Eye className="w-3.5 h-3.5 text-muted-foreground" /></button>
                          <button onClick={e => { e.stopPropagation(); setMoveDialogItems([{ id: doc.id, type: "document", nom: doc.titre }]); }} className="p-1 rounded hover:bg-muted"><MoveRight className="w-3.5 h-3.5 text-muted-foreground" /></button>
                          <button onClick={e => { e.stopPropagation(); deleteItem({ id: doc.id, type: "document", nom: doc.titre, data: doc }); }} className="p-1 rounded hover:bg-muted"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x} y={contextMenu.y} item={contextMenu.item}
          onClose={() => setContextMenu(null)}
          onRename={() => setInlineRename({ type: contextMenu.item.type, id: contextMenu.item.id, name: contextMenu.item.nom })}
          onMove={() => setMoveDialogItems([{ id: contextMenu.item.id, type: contextMenu.item.type, nom: contextMenu.item.nom }])}
          onDelete={() => deleteItem(contextMenu.item)}
          onDownload={contextMenu.item.type === "document" ? () => downloadDoc(contextMenu.item.data as Document) : undefined}
          onVisibility={contextMenu.item.type === "document" ? () => setVisibilityDialogIds([contextMenu.item.id]) : undefined}
          onPreview={contextMenu.item.type === "document" ? () => setLecturesDocId(contextMenu.item.id) : undefined}
        />
      )}

      {/* Bulk Action Bar */}
      {selectedItems.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-card border rounded-full shadow-xl px-4 py-2">
          <span className="text-sm font-medium">{selectedItems.length} sélectionné(s)</span>
          <Button size="sm" variant="outline" className="gap-1.5 rounded-full h-7" onClick={() => setMoveDialogItems(selectedItems)}>
            <MoveRight className="w-3.5 h-3.5" />Déplacer
          </Button>
          {selectedItems.every(i => i.type === "document") && (
            <Button size="sm" variant="outline" className="gap-1.5 rounded-full h-7" onClick={() => setVisibilityDialogIds(selectedItems.map(i => i.id))}>
              <CheckSquare className="w-3.5 h-3.5" />Visibilité
            </Button>
          )}
          <Button size="sm" variant="outline" className="gap-1.5 rounded-full h-7 text-destructive hover:text-destructive"
            onClick={async () => {
              if (!confirm(`Supprimer ${selectedItems.length} élément(s) ?`)) return;
              for (const it of selectedItems) {
                if (it.type === "document") {
                  const doc = content?.documents.find(d => d.id === it.id);
                  const endpoint = doc?.storageBucket === "partenaire-medias" ? "videos" : "documents";
                  const response = await authFetch(`/api/partenaires/${endpoint}/${it.id}`, { method: "DELETE" });
                  if (!response.ok) { const error = await response.json(); toast({ title: error.message || "Suppression impossible", variant: "destructive" }); return; }
                }
                else { const r = await authFetch(`/api/partenaires/categories/${it.id}`, { method: "DELETE" }); if (!r.ok) { const d = await r.json(); toast({ title: d.message, variant: "destructive" }); } }
              }
              invalidateDocumentCaches(qc);
              setSelectedKeys(new Set());
            }}>
            <Trash2 className="w-3.5 h-3.5" />Supprimer
          </Button>
          <button onClick={() => setSelectedKeys(new Set())} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Global drag overlay */}
      {globalDragOver && (
        <div className="fixed inset-0 z-50 bg-primary/10 backdrop-blur-sm flex items-center justify-center pointer-events-none border-4 border-dashed border-primary/50 rounded-xl m-2">
          <div className="flex flex-col items-center gap-3 text-primary">
            <Upload className="w-12 h-12" />
            <p className="text-xl font-semibold">Déposez vos fichiers ici</p>
            <p className="text-sm text-muted-foreground">Ils seront uploadés dans le dossier courant</p>
          </div>
        </div>
      )}

      {/* Upload Queue */}
      {uploads.length > 0 && <UploadQueue uploads={uploads} onClose={() => setUploads([])} />}

      {/* Dialogs */}
      {videoPreview && (
        <Dialog open onOpenChange={open => { if (!open) setVideoPreview(null); }}>
          <DialogContent className="w-[calc(100%-2rem)] max-w-2xl min-w-0">
            <DialogHeader><DialogTitle className="break-words">{videoPreview.doc.titre}</DialogTitle></DialogHeader>
            <PartenaireVideoPlayer key={videoPreview.url} url={videoPreview.url} title={videoPreview.doc.titre} />
          </DialogContent>
        </Dialog>
      )}
      {docModalData && (
        <DocumentModal open={!!docModalData} onClose={() => setDocModalData(null)} editing={docModalData.editing} categories={allCategories} resellers={resellers} currentFolderId={currentFolderId} onSaved={() => setDocModalData(null)} />
      )}
      {moveDialogItems && (
        <MoveDialog open={!!moveDialogItems} onClose={() => setMoveDialogItems(null)} items={moveDialogItems} categories={allCategories} onMoved={() => { setMoveDialogItems(null); setSelectedKeys(new Set()); }} />
      )}
      {visibilityDialogIds && (
        <VisibilityDialog open={!!visibilityDialogIds} onClose={() => setVisibilityDialogIds(null)} docIds={visibilityDialogIds} resellers={resellers} onDone={() => { setVisibilityDialogIds(null); setSelectedKeys(new Set()); }} />
      )}
      {lecturesDocId && <LecturesPanel open={!!lecturesDocId} onClose={() => setLecturesDocId(null)} docId={lecturesDocId} />}
    </div>
  );
}

// ── Actualité Preview Panel ────────────────────────────────────────────────────
function ActuPreviewPanel({ actu, onClose, onEdit }: { actu: Actualite; onClose: () => void; onEdit: () => void }) {
  const visiLabels: Record<string, string> = {
    tous: "Tous les partenaires",
    avec_contrat: "Avec contrat actif",
    selection: "Sélection manuelle",
  };
  const typeMap: Record<string, { label: string; color: string }> = {
    nouveaute: { label: "Nouveauté", color: "bg-blue-100 text-blue-700" },
    promotion: { label: "Promotion", color: "bg-orange-100 text-orange-700" },
    information: { label: "Information", color: "bg-slate-100 text-slate-600" },
  };
  const t = typeMap[actu.type] || typeMap.information;
  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString("fr-FR") : null;
  const economie = actu.prixNormal && actu.prixPromo
    ? (parseFloat(actu.prixNormal) - parseFloat(actu.prixPromo)).toFixed(2)
    : null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] flex flex-col overflow-hidden p-0">
        {/* Image de couverture */}
        {actu.imageUrl && (
          <div className="relative w-full h-52 bg-muted overflow-hidden rounded-t-lg flex-shrink-0">
            <img
              src={actu.imageUrl}
              alt={actu.titre}
              className="w-full h-full object-cover"
              onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        )}

        <div className="flex flex-col overflow-y-auto">
          {/* En-tête */}
          <div className="px-6 pt-5 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs px-2 py-0.5 rounded font-medium ${t.color}`}>{t.label}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${actu.actif ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}>
                {actu.actif ? "Actif" : "Inactif"}
              </span>
            </div>
            <h2 className="text-xl font-bold leading-tight">{actu.titre}</h2>
            {actu.accroche && <p className="text-muted-foreground text-sm mt-1">{actu.accroche}</p>}
          </div>

          {/* Bloc promotion */}
          {actu.type === "promotion" && (actu.prixPromo || actu.prixNormal) && (
            <div className="mx-6 mb-4 p-4 rounded-lg border bg-orange-50 dark:bg-orange-950/20">
              <div className="flex items-baseline gap-3">
                {actu.prixPromo && (
                  <span className="text-2xl font-bold text-orange-600">{actu.prixPromo} €</span>
                )}
                {actu.prixNormal && (
                  <span className="text-base line-through text-muted-foreground">{actu.prixNormal} €</span>
                )}
                {economie && (
                  <span className="text-sm font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">
                    −{economie} €
                  </span>
                )}
              </div>
              {(actu.dateDebut || actu.dateFin) && (
                <div className="text-xs text-muted-foreground mt-2 flex gap-3">
                  {actu.dateDebut && <span>Du {formatDate(actu.dateDebut)}</span>}
                  {actu.dateFin && <span>au {formatDate(actu.dateFin)}</span>}
                </div>
              )}
            </div>
          )}

          {/* Contenu */}
          {actu.contenu && (
            <div className="px-6 pb-4">
              <p className="text-sm font-medium mb-1 text-muted-foreground">Contenu</p>
              <div className="text-sm whitespace-pre-wrap leading-relaxed border rounded-md p-3 bg-muted/20">
                {actu.contenu}
              </div>
            </div>
          )}

          {/* Méta */}
          <div className="px-6 pb-4 flex items-center gap-4 text-xs text-muted-foreground border-t pt-3">
            <span>Visibilité : <span className="font-medium text-foreground">{visiLabels[actu.visibilite] ?? actu.visibilite}</span></span>
            {!actu.dateDebut && !actu.dateFin && actu.type !== "promotion" && actu.dateDebut && (
              <span>Publié le {formatDate(actu.createdAt)}</span>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 pb-5 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Fermer</Button>
            <Button size="sm" onClick={onEdit}><Pencil className="w-3.5 h-3.5 mr-1" />Modifier</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function PartenairesList() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [actuModalOpen, setActuModalOpen] = useState(false);
  const [editingActu, setEditingActu] = useState<Actualite | null>(null);
  const [lecturesActuId, setLecturesActuId] = useState<string | null>(null);
  const [previewActu, setPreviewActu] = useState<Actualite | null>(null);

  const { data: resellers = [] } = useQuery<Reseller[]>({
    queryKey: ["/api/partenaires/resellers"],
    queryFn: async () => { const r = await authFetch("/api/partenaires/resellers"); return r.json(); },
  });

  const { data: actualites = [] } = useQuery<Actualite[]>({
    queryKey: ["/api/partenaires/actualites"],
    queryFn: async () => { const r = await authFetch("/api/partenaires/actualites"); return r.json(); },
  });

  const { data: allDocuments = [] } = useQuery<Document[]>({
    queryKey: ["/api/partenaires/documents"],
    queryFn: async () => { const r = await authFetch("/api/partenaires/documents"); return r.json(); },
  });

  const deleteActu = useMutation({
    mutationFn: async (id: string) => authFetch(`/api/partenaires/actualites/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/partenaires/actualites"] }),
  });

  const toggleActif = async (actu: Actualite) => {
    await authFetch(`/api/partenaires/actualites/${actu.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...actu, actif: !actu.actif }),
    });
    qc.invalidateQueries({ queryKey: ["/api/partenaires/actualites"] });
  };

  const typeBadge = (type: string) => {
    const map: Record<string, { label: string; color: string }> = {
      nouveaute: { label: "Nouveauté", color: "bg-blue-100 text-blue-700" },
      promotion: { label: "Promotion", color: "bg-orange-100 text-orange-700" },
      information: { label: "Information", color: "bg-slate-100 text-slate-600" },
    };
    const t = map[type] || map.information;
    return <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${t.color}`}>{t.label}</span>;
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FolderOpen className="w-6 h-6 text-primary" />
          Espace partenaires
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Gérez les documents, actualités et promotions destinés aux revendeurs partenaires.</p>
      </div>

      <Tabs defaultValue="documents">
        <TabsList className="mb-4">
          <TabsTrigger value="documents" data-testid="tab-documents">
            <FileText className="w-4 h-4 mr-1.5" />Documents
          </TabsTrigger>
          <TabsTrigger value="actualites" data-testid="tab-actualites">
            <Megaphone className="w-4 h-4 mr-1.5" />Actualités & Promotions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents">
          <DocumentExplorer resellers={resellers} />
        </TabsContent>

        <TabsContent value="actualites">
          <div className="border rounded-lg bg-card overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b">
              <span className="text-sm font-semibold">{actualites.length} actualité(s)</span>
              <Button size="sm" onClick={() => { setEditingActu(null); setActuModalOpen(true); }} data-testid="btn-new-actualite">
                <Plus className="w-4 h-4 mr-1" />Nouvelle actualité
              </Button>
            </div>
            {actualites.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-2">
                <Megaphone className="w-10 h-10 opacity-20" />
                <span>Aucune actualité pour le moment</span>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className="px-3 py-2 w-12" />
                    <th className="text-left px-3 py-2 font-medium">Type</th>
                    <th className="text-left px-3 py-2 font-medium">Titre</th>
                    <th className="text-left px-3 py-2 font-medium">Visibilité</th>
                    <th className="text-left px-3 py-2 font-medium">Dates</th>
                    <th className="text-left px-3 py-2 font-medium">Lectures</th>
                    <th className="text-left px-3 py-2 font-medium">Statut</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {actualites.map(actu => (
                    <tr key={actu.id} className="border-b hover:bg-muted/30 transition-colors" data-testid={`row-actu-${actu.id}`}>
                      <td className="px-3 py-2">
                        {actu.imageUrl ? (
                          <img
                            src={actu.imageUrl}
                            alt=""
                            className="w-10 h-10 rounded object-cover border bg-muted"
                            onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded border bg-muted flex items-center justify-center">
                            <ImageIcon className="w-4 h-4 text-muted-foreground/40" />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">{typeBadge(actu.type)}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium truncate max-w-[220px]">{actu.titre}</div>
                        {actu.accroche && <div className="text-xs text-muted-foreground truncate max-w-[220px]">{actu.accroche}</div>}
                        {actu.type === "promotion" && actu.prixPromo && (
                          <div className="text-xs text-orange-600 font-medium">{actu.prixPromo} € <span className="line-through text-muted-foreground">{actu.prixNormal} €</span></div>
                        )}
                      </td>
                      <td className="px-3 py-2"><VisiBadge v={actu.visibilite} /></td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {actu.dateDebut && <div>Début : {formatDate(actu.dateDebut)}</div>}
                        {actu.dateFin && <div>Fin : {formatDate(actu.dateFin)}</div>}
                        {!actu.dateDebut && !actu.dateFin && "—"}
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => setLecturesActuId(actu.id)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary" data-testid={`btn-lectures-actu-${actu.id}`}>
                          <Eye className="w-3 h-3" />{actu.nbLectures}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${actu.actif ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}>
                          {actu.actif ? "Actif" : "Inactif"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setPreviewActu(actu)}
                            className="p-1 rounded hover:bg-muted"
                            title="Aperçu"
                            data-testid={`btn-preview-actu-${actu.id}`}
                          >
                            <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                          <button onClick={() => toggleActif(actu)} className="p-1 rounded hover:bg-muted" data-testid={`btn-toggle-actu-${actu.id}`}><ToggleLeft className="w-3.5 h-3.5 text-muted-foreground" /></button>
                          <button onClick={() => { setEditingActu(actu); setActuModalOpen(true); }} className="p-1 rounded hover:bg-muted" data-testid={`btn-edit-actu-${actu.id}`}><Pencil className="w-3.5 h-3.5 text-muted-foreground" /></button>
                          <button onClick={() => { if (confirm(`Supprimer "${actu.titre}" ?`)) deleteActu.mutate(actu.id); }} className="p-1 rounded hover:bg-muted" data-testid={`btn-delete-actu-${actu.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {actuModalOpen && (
            <ActualiteModal open={actuModalOpen} onClose={() => setActuModalOpen(false)} editing={editingActu} documents={allDocuments} onSaved={() => {}} />
          )}
          {lecturesActuId && <LecturesPanel open={!!lecturesActuId} onClose={() => setLecturesActuId(null)} actId={lecturesActuId} />}
          {previewActu && (
            <ActuPreviewPanel
              actu={previewActu}
              onClose={() => setPreviewActu(null)}
              onEdit={() => {
                setEditingActu(previewActu);
                setActuModalOpen(true);
                setPreviewActu(null);
              }}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
